import { UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOktaAuth } from "@okta/okta-react";
import PhoneVerificationService, {
  PhoneVerificationStatus,
} from "../../../PhoneVerificationService";
import toastMessages from "../../helpers/toastMessage";
import { getApiErrorMessage } from "../../../../utils/helpers/communicationPreferencesHelpers";

function useAccessToken(): string | undefined {
  const { authState } = useOktaAuth();
  return authState?.accessToken?.accessToken;
}

export function usePhoneVerificationStatus(): UseQueryResult<PhoneVerificationStatus> {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["phoneVerificationStatus"],
    queryFn: () => PhoneVerificationService.getStatus(token!),
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSendPhoneVerificationCode() {
  const token = useAccessToken();

  return useMutation({
    mutationKey: ["sendPhoneVerificationCode"],
    mutationFn: (mobilePhone?: string) => {
      if (!token) throw new Error("Not authenticated");
      return PhoneVerificationService.sendCode(token, mobilePhone);
    },
    onSuccess: () => {
      toastMessages.success("Verification code sent.");
    },
    onError: (error: unknown) => {
      toastMessages.error(
        getApiErrorMessage(error, "Failed to send verification code.")
      );
    },
  });
}

export function useVerifyPhoneCode() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["verifyPhoneCode"],
    mutationFn: (code: string) => {
      if (!token) throw new Error("Not authenticated");
      return PhoneVerificationService.verifyCode(token, code);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phoneVerificationStatus"] });
      toastMessages.success("Phone number verified.");
    },
    onError: (error: unknown) => {
      toastMessages.error(
        getApiErrorMessage(error, "Failed to verify code.")
      );
    },
  });
}
